/**
 * Pure stat-tile alert evaluator (S12) — no React, no I/O. Fires on the CROSS
 * from armed → firing (never repeatedly while the condition stays matched),
 * re-arms the instant the condition stops matching, and `cooldownSeconds`
 * gates a repeat notification measured from the last time a notification
 * ACTUALLY fired (a suppressed notify does not reset the cooldown clock, or
 * the debounce would never expire).
 *
 * `TileAlertState` is the ENTIRE carry-over between evaluations — nothing
 * else is memoized here — because it round-trips through the
 * `DashboardUiState` sidecar (`alertState`, keyed by tile id) and
 * "fire-once-until-cleared" must survive an app restart through that file
 * alone: a fresh call after relaunch, fed only the deserialized state blob,
 * must behave identically to the in-session call that produced it.
 */
import type { TileAlertRule, TileAlertState } from '../types/dashboardTypes';

/** The 6-operator alert set (superset of `TileThreshold`'s 4 — adds ==/!=). */
export function matchesAlertRule(
  value: number,
  rule: Pick<TileAlertRule, 'operator' | 'threshold'>,
): boolean {
  switch (rule.operator) {
    case '>':
      return value > rule.threshold;
    case '>=':
      return value >= rule.threshold;
    case '<':
      return value < rule.threshold;
    case '<=':
      return value <= rule.threshold;
    case '==':
      return value === rule.threshold;
    case '!=':
      return value !== rule.threshold;
  }
}

export type AlertEvaluationResult = {
  nextState: TileAlertState;
  /** True exactly once per armed→firing cross, gated by `cooldownSeconds`. */
  shouldNotify: boolean;
};

/**
 * `evaluate(rule, previousState, scalarValue, now) → {nextState, shouldNotify}`
 * — call this on every settled stat-tile value while an `alertRule` exists.
 * Sustained matches and sustained non-matches are both no-ops beyond
 * bookkeeping; only the transition edges do anything.
 */
export function evaluateAlert(
  rule: TileAlertRule,
  previousState: TileAlertState | undefined,
  value: number,
  now: Date,
): AlertEvaluationResult {
  const wasFiring = previousState?.status === 'firing';
  const matches = matchesAlertRule(value, rule);
  const nowIso = now.toISOString();

  if (!matches) {
    if (!wasFiring) {
      // Already armed (or no state yet) — nothing changes.
      return { nextState: previousState ?? { status: 'armed' }, shouldNotify: false };
    }
    // Cross firing → armed: re-arm, remember when it cleared. `lastFiredAt`
    // carries forward so the NEXT cross can still measure the cooldown gap.
    return {
      nextState: { status: 'armed', lastFiredAt: previousState?.lastFiredAt, clearedAt: nowIso },
      shouldNotify: false,
    };
  }

  if (wasFiring) {
    // Sustained match — never repeat the notification, state is unchanged.
    return { nextState: previousState!, shouldNotify: false };
  }

  // Cross armed → firing.
  const lastFiredAt = previousState?.lastFiredAt;
  const cooldownMs = Math.max(0, rule.cooldownSeconds) * 1000;
  const withinCooldown =
    lastFiredAt !== undefined && now.getTime() - new Date(lastFiredAt).getTime() < cooldownMs;

  return {
    nextState: {
      status: 'firing',
      // Suppressed (within-cooldown) fires do NOT reset the clock — keep the
      // OLD lastFiredAt so the debounce window is measured from the last
      // real notification, not the last suppressed attempt.
      lastFiredAt: withinCooldown ? lastFiredAt : nowIso,
      clearedAt: previousState?.clearedAt,
    },
    shouldNotify: !withinCooldown,
  };
}

/** Scheduler's background budget (S3) — kept as the single source of truth;
 * `useDashboardScheduler` imports this for its `backgroundBudget.maxTiles`. */
export const BACKGROUND_ALERT_MAX_TILES = 5;

export type BackgroundAlertBudgetResult = { allowed: true } | { allowed: false; reason: string };

/**
 * Gate for the AlertRuleDialog's "Always, up to every 60s" toggle. The
 * scheduler privileges only the FIRST `maxTiles` background-alert tiles it
 * registers (FIFO, `services/refreshScheduler.ts`) — a 6th tile enabling
 * `background: true` would silently never actually run while hidden. Reject
 * it explicitly, with copy, instead of shipping a setting that quietly does
 * nothing.
 */
export function canEnableBackgroundAlert(
  otherBackgroundTileCount: number,
  maxTiles: number = BACKGROUND_ALERT_MAX_TILES,
): BackgroundAlertBudgetResult {
  if (otherBackgroundTileCount < maxTiles) return { allowed: true };
  return {
    allowed: false,
    reason: `Background alerts are limited to ${maxTiles} tiles per dashboard — turn one off on another tile first.`,
  };
}
